import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ClockIcon, PhoneIcon, MailIcon, GlobeIcon, ArrowLeftIcon, CheckCircleIcon, StarIcon, UsersIcon, TagIcon, FilmIcon, VideoIcon, AwardIcon, MessageSquareIcon, ChevronDownIcon } from 'lucide-react';
import { getBusinessBySlug, getRelatedBusinesses } from '../data/businesses';
import { Footer } from '../components/Footer';
interface BusinessDetailPageProps {
  slug: string;
  onNavigate: (page: 'home' | 'about' | 'business', slug?: string) => void;
}
export function BusinessDetailPage({
  slug,
  onNavigate
}: BusinessDetailPageProps) {
  const business = getBusinessBySlug(slug);
  const relatedBusinesses = getRelatedBusinesses(slug);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [showAllMovies, setShowAllMovies] = useState(false);
  if (!business) {
    return <div className="min-h-screen flex items-center justify-center bg-white pt-16" data-id="element-282">
        <div className="text-center" data-id="element-283">
          <h2 className="font-display text-[32px] font-medium text-[#1d1d1f] mb-4" data-id="element-284">
            Business not found
          </h2>
          <button onClick={() => onNavigate('home')} className="text-[#c9a961] font-medium hover:underline" data-id="element-285">
            Return Home
          </button>
        </div>
      </div>;
  }
  const galleryImages = business.gallery || [];
  const visibleGallery = showAllGallery ? galleryImages : galleryImages.slice(0, 6);
  const moviesList = business.movies || [];
  const visibleMovies = showAllMovies ? moviesList : moviesList.slice(0, 20);
  const hasRichContent = business.movies || business.tvcs || business.gallery;
  return <main className="min-h-screen bg-white" data-id="element-286">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] w-full overflow-hidden bg-black" data-id="element-287">
        {business.image && <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 animate-[zoom_20s_infinite_alternate]" style={{
        backgroundImage: `url('${business.image}')`,
        transform: 'scale(1.1)'
      }} data-id="element-288" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" data-id="element-289" />

        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-16 md:pb-24" data-id="element-290">
          <motion.button initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} onClick={() => onNavigate('home')} className="absolute top-24 left-6 text-white/60 hover:text-[#c9a961] text-[14px] uppercase tracking-wider font-medium flex items-center gap-2 transition-colors duration-300" data-id="element-291">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-292" />
            Back to Portfolio
          </motion.button>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-293">
            <span className="inline-block px-4 py-1.5 rounded-sm bg-[#c9a961] text-black text-[12px] uppercase tracking-widest font-bold mb-6" data-id="element-294">
              {business.category}
            </span>
            <h1 className="font-display text-white text-[48px] md:text-[72px] lg:text-[88px] font-medium leading-[1.1] tracking-tight mb-6" data-id="element-295">
              {business.name}
            </h1>
            {hasRichContent && <p className="text-white/80 text-[18px] md:text-[20px] mb-8 max-w-2xl font-light leading-relaxed" data-id="element-296">
                {business.description}
              </p>}
            <div className="flex flex-wrap gap-8 text-white/80 text-[15px] tracking-wide" data-id="element-297">
              <div className="flex items-center gap-2" data-id="element-298">
                <MapPinIcon className="w-5 h-5 text-[#c9a961]" data-id="element-299" />
                {business.location}
              </div>
              {business.establishedYear && <div className="flex items-center gap-2" data-id="element-300">
                  <AwardIcon className="w-5 h-5 text-[#c9a961]" data-id="element-301" />
                  Est. {business.establishedYear}
                </div>}
              {business.hours && <div className="flex items-center gap-2" data-id="element-302">
                  <ClockIcon className="w-5 h-5 text-[#c9a961]" data-id="element-303" />
                  {business.hours}
                </div>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      {hasRichContent && <section className="bg-[#1d1d1f] py-16 px-6 border-t border-white/5" data-id="element-304">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16" data-id="element-305">
            {business.movies && <div className="text-center" data-id="element-306">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-white leading-none mb-2" data-id="element-307">
                  {business.movies.length}+
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-308">
                  Films & Productions
                </div>
              </div>}
            {business.tvcs && <div className="text-center" data-id="element-309">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-white leading-none mb-2" data-id="element-310">
                  {business.tvcs.length}+
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-311">
                  TV Commercials
                </div>
              </div>}
            {business.gallery && <div className="text-center" data-id="element-312">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-white leading-none mb-2" data-id="element-313">
                  500+
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-314">
                  Projects Completed
                </div>
              </div>}
            {business.establishedYear && <div className="text-center" data-id="element-315">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-[#c9a961] leading-none mb-2" data-id="element-316">
                  {business.establishedYear}
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-317">
                  Established
                </div>
              </div>}
          </div>
        </section>}

      {/* Quick Info Bar */}
      <section className="bg-[#faf8f5] py-10 px-6 border-b border-[#c9a961]/10" data-id="element-318">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-319">
          <div className="flex items-center gap-4" data-id="element-320">
            <TagIcon className="w-5 h-5 text-[#c9a961]" data-id="element-321" />
            <div data-id="element-322">
              <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-323">
                Category
              </span>
              <span className="text-[#1d1d1f] font-medium text-[15px]" data-id="element-324">
                {business.category}
              </span>
            </div>
          </div>
          {business.hours && <div className="flex items-center gap-4" data-id="element-325">
              <ClockIcon className="w-5 h-5 text-[#c9a961]" data-id="element-326" />
              <div data-id="element-327">
                <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-328">
                  Hours
                </span>
                <span className="text-[#1d1d1f] font-medium text-[15px]" data-id="element-329">
                  {business.hours}
                </span>
              </div>
            </div>}
          {business.capacity && <div className="flex items-center gap-4" data-id="element-330">
              <UsersIcon className="w-5 h-5 text-[#c9a961]" data-id="element-331" />
              <div data-id="element-332">
                <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-333">
                  Capacity
                </span>
                <span className="text-[#1d1d1f] font-medium text-[15px]" data-id="element-334">
                  {business.capacity} Guests
                </span>
              </div>
            </div>}
          {business.contactNumber && <div className="flex items-center gap-4" data-id="element-335">
              <PhoneIcon className="w-5 h-5 text-[#c9a961]" data-id="element-336" />
              <div data-id="element-337">
                <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-338">
                  Contact
                </span>
                <a href={`tel:${business.contactNumber.split('/')[0].trim()}`} className="text-[#1d1d1f] font-medium text-[15px] hover:text-[#c9a961] transition-colors" data-id="element-339">
                  {business.contactNumber.split('/')[0].trim()}
                </a>
              </div>
            </div>}
        </div>
      </section>

      {/* Content Grid */}
      <section className="py-24 md:py-32 px-6 bg-white" data-id="element-340">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20" data-id="element-341">
          {/* Main Content */}
          <div className="lg:col-span-8" data-id="element-342">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-343">
              <span className="text-[#c9a961] text-[13px] font-bold tracking-[0.2em] uppercase mb-4 block" data-id="element-344">
                Overview
              </span>
              <h2 className="font-display text-[32px] md:text-[48px] font-medium text-[#1d1d1f] mb-6" data-id="element-345">
                About The Venue
              </h2>
              <p className="text-[#86868b] text-[18px] md:text-[20px] leading-[1.8] font-light" data-id="element-346">
                {business.longDescription || business.description}
              </p>
            </motion.div>

            {/* Services/Features */}
            {(business.services || business.features) && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-347">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8" data-id="element-348">
                  {business.category === 'Entertainment' ? 'Our Services' : 'What We Offer'}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-349">
                  {[...(business.services || []), ...(business.features || [])].map((item, idx) => <div key={idx} className="flex items-center gap-4 p-5 rounded-sm bg-[#faf8f5] border border-[#f0ebe3]" data-id="element-350">
                      <CheckCircleIcon className="w-5 h-5 text-[#c9a961] shrink-0" data-id="element-351" />
                      <span className="text-[#1d1d1f] text-[15px] font-medium" data-id="element-352">
                        {item}
                      </span>
                    </div>)}
                </div>
              </motion.div>}

            {/* Gallery */}
            {galleryImages.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-353">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8" data-id="element-354">
                  Gallery
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-355">
                  {visibleGallery.map((img, idx) => <div key={idx} className="aspect-[4/3] rounded-sm overflow-hidden bg-[#faf8f5] relative group" data-id="element-356">
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-357" />
                      {img.caption && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-358">
                          <p className="text-white text-[13px] font-medium tracking-wide" data-id="element-359">
                            {img.caption}
                          </p>
                        </div>}
                    </div>)}
                </div>
                {galleryImages.length > 6 && <button onClick={() => setShowAllGallery(!showAllGallery)} className="mt-8 mx-auto flex items-center gap-2 text-[#c9a961] font-medium text-[14px] uppercase tracking-wide hover:gap-3 transition-all duration-300" data-id="element-360">
                    {showAllGallery ? 'Show Less' : `View All ${galleryImages.length} Photos`}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showAllGallery ? 'rotate-180' : ''}`} data-id="element-361" />
                  </button>}
              </motion.div>}

            {/* Filmography */}
            {moviesList.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-362">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8 flex items-center gap-3" data-id="element-363">
                  <FilmIcon className="w-6 h-6 text-[#c9a961]" data-id="element-364" />
                  Filmography
                </h2>
                <div className="grid sm:grid-cols-2 gap-x-12 gap-y-0" data-id="element-365">
                  {visibleMovies.map((movie, idx) => <div key={idx} className="flex items-center gap-4 py-4 border-b border-[#f0ebe3]" data-id="element-366">
                      <span className="text-[#c9a961] text-[13px] font-mono w-6 shrink-0" data-id="element-367">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[#1d1d1f] text-[15px]" data-id="element-368">
                        {movie}
                      </span>
                    </div>)}
                </div>
                {moviesList.length > 20 && <button onClick={() => setShowAllMovies(!showAllMovies)} className="mt-8 flex items-center gap-2 text-[#c9a961] font-medium text-[14px] uppercase tracking-wide hover:gap-3 transition-all duration-300" data-id="element-369">
                    {showAllMovies ? 'Show Less' : `View All ${moviesList.length} Productions`}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showAllMovies ? 'rotate-180' : ''}`} data-id="element-370" />
                  </button>}
              </motion.div>}

            {/* TVCs */}
            {business.tvcs && business.tvcs.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-371">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8 flex items-center gap-3" data-id="element-372">
                  <VideoIcon className="w-6 h-6 text-[#c9a961]" data-id="element-373" />
                  Television Commercials
                </h2>
                <div className="flex flex-wrap gap-3" data-id="element-374">
                  {business.tvcs.map((tvc, idx) => <span key={idx} className="px-5 py-2 bg-[#faf8f5] border border-[#f0ebe3] rounded-full text-[#1d1d1f] text-[14px] font-medium" data-id="element-375">
                      {tvc}
                    </span>)}
                </div>
              </motion.div>}

            {/* Reviews */}
            {business.reviews && business.reviews.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-376">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8" data-id="element-377">
                  Guest Reviews
                </h2>
                <div className="grid gap-8" data-id="element-378">
                  {business.reviews.map((review, idx) => <div key={idx} className="p-8 rounded-sm bg-[#faf8f5] border border-[#f0ebe3]" data-id="element-379">
                      <div className="flex gap-1 mb-6" data-id="element-380">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#c9a961] text-[#c9a961]' : 'text-[#d2d2d7]'}`} data-id="element-381" />)}
                      </div>
                      <p className="text-[#1d1d1f] text-[18px] font-light italic leading-relaxed mb-6" data-id="element-382">
                        "{review.text}"
                      </p>
                      <div className="flex justify-between items-center border-t border-[#c9a961]/10 pt-4" data-id="element-383">
                        <span className="font-display font-medium text-[#1d1d1f] text-[16px]" data-id="element-384">
                          {review.author}
                        </span>
                        <span className="text-[#86868b] text-[13px] uppercase tracking-wide" data-id="element-385">
                          {review.date}
                        </span>
                      </div>
                    </div>)}
                </div>
              </motion.div>}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-386">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="sticky top-24 space-y-8" data-id="element-387">
              <div className="p-8 rounded-sm bg-[#faf8f5] border border-[#f0ebe3]" data-id="element-388">
                <h3 className="font-display text-[20px] font-medium text-[#1d1d1f] mb-6" data-id="element-389">
                  Contact Information
                </h3>
                <div className="space-y-6" data-id="element-390">
                  <div className="flex gap-4" data-id="element-391">
                    <MapPinIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-392" />
                    <div data-id="element-393">
                      <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-394">
                        Address
                      </span>
                      <span className="text-[#1d1d1f] text-[15px] leading-relaxed" data-id="element-395">
                        {business.address}
                      </span>
                    </div>
                  </div>

                  {business.managingDirector && <div className="flex gap-4" data-id="element-396">
                      <UsersIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-397" />
                      <div data-id="element-398">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-399">
                          Managing Director
                        </span>
                        <span className="text-[#1d1d1f] text-[15px]" data-id="element-400">
                          {business.managingDirector}
                        </span>
                      </div>
                    </div>}

                  {business.contactPerson && !business.managingDirector && <div className="flex gap-4" data-id="element-401">
                      <UsersIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-402" />
                      <div data-id="element-403">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-404">
                          Contact Person
                        </span>
                        <span className="text-[#1d1d1f] text-[15px]" data-id="element-405">
                          {business.contactPerson}
                        </span>
                      </div>
                    </div>}

                  {business.contactNumber && <div className="flex gap-4" data-id="element-406">
                      <PhoneIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-407" />
                      <div data-id="element-408">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-409">
                          Phone
                        </span>
                        <a href={`tel:${business.contactNumber.split('/')[0].trim()}`} className="text-[#1d1d1f] text-[15px] hover:text-[#c9a961] transition-colors" data-id="element-410">
                          {business.contactNumber}
                        </a>
                      </div>
                    </div>}

                  {business.email && <div className="flex gap-4" data-id="element-411">
                      <MailIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-412" />
                      <div data-id="element-413">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-414">
                          Email
                        </span>
                        <a href={`mailto:${business.email}`} className="text-[#1d1d1f] text-[15px] hover:text-[#c9a961] transition-colors break-all" data-id="element-415">
                          {business.email}
                        </a>
                      </div>
                    </div>}

                  {business.lineId && <div className="flex gap-4" data-id="element-416">
                      <MessageSquareIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-417" />
                      <div data-id="element-418">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-419">
                          Line ID
                        </span>
                        <span className="text-[#1d1d1f] text-[15px]" data-id="element-420">
                          {business.lineId}
                        </span>
                      </div>
                    </div>}

                  {business.website && <div className="flex gap-4" data-id="element-421">
                      <GlobeIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-422" />
                      <div data-id="element-423">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-424">
                          Website
                        </span>
                        <a href={`https://${business.website}`} target="_blank" rel="noopener noreferrer" className="text-[#1d1d1f] text-[15px] hover:text-[#c9a961] transition-colors" data-id="element-425">
                          {business.website}
                        </a>
                      </div>
                    </div>}
                </div>
              </div>

              {business.registrations && <div className="p-8 rounded-sm bg-white border border-[#e5e5e5]" data-id="element-426">
                  <h3 className="font-display text-[20px] font-medium text-[#1d1d1f] mb-6 flex items-center gap-2" data-id="element-427">
                    <AwardIcon className="w-5 h-5 text-[#c9a961]" data-id="element-428" />
                    Registrations
                  </h3>
                  <ul className="space-y-4" data-id="element-429">
                    {business.registrations.map((reg, idx) => <li key={idx} className="flex items-center gap-3 text-[#1d1d1f] text-[15px]" data-id="element-430">
                        <CheckCircleIcon className="w-4 h-4 text-[#c9a961]" data-id="element-431" />
                        {reg}
                      </li>)}
                  </ul>
                </div>}

              {business.establishedYear && <div className="p-8 rounded-sm bg-[#1d1d1f] text-white" data-id="element-432">
                  <span className="block text-white/50 text-[11px] uppercase tracking-widest mb-2" data-id="element-433">
                    Established
                  </span>
                  <div className="font-display text-[48px] font-medium leading-none mb-1 text-[#c9a961]" data-id="element-434">
                    {business.establishedYear}
                  </div>
                  <div className="text-white/60 text-[15px]" data-id="element-435">
                    Bangkok, Thailand
                  </div>
                </div>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Businesses */}
      {relatedBusinesses.length > 0 && <section className="py-24 md:py-32 px-6 bg-[#faf8f5] border-t border-[#c9a961]/10" data-id="element-436">
          <div className="max-w-7xl mx-auto" data-id="element-437">
            <h2 className="font-display text-[32px] md:text-[48px] font-medium text-[#1d1d1f] mb-4 text-center" data-id="element-438">
              More {business.category}
            </h2>
            <div className="w-16 h-[1px] bg-[#c9a961] mx-auto mb-8" data-id="element-439" />
            <p className="text-[#86868b] text-[18px] max-w-3xl mx-auto text-center mb-16 font-light" data-id="element-440">
              Explore similar venues in our portfolio
            </p>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-441">
              {relatedBusinesses.map((related, idx) => <motion.div key={related.slug} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} onClick={() => onNavigate('business', related.slug)} className="group cursor-pointer bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border-b-2 border-transparent hover:border-[#c9a961]" data-id="element-442">
                  <div className="h-56 bg-[#1d1d1f] relative overflow-hidden" data-id="element-443">
                    {related.image ? <div className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110" style={{
                backgroundImage: `url('${related.image}')`
              }} data-id="element-444" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-445">
                        <span className="font-display text-[48px] text-white/20" data-id="element-446">
                          {related.name.charAt(0)}
                        </span>
                      </div>}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-447" />
                  </div>
                  <div className="p-8" data-id="element-448">
                    <div className="flex items-center gap-2 text-[#86868b] text-[13px] mb-3 uppercase tracking-wide" data-id="element-449">
                      <MapPinIcon className="w-3.5 h-3.5 text-[#c9a961]" data-id="element-450" />
                      {related.location}
                    </div>
                    <h3 className="font-display text-[20px] font-medium text-[#1d1d1f] mb-3 group-hover:text-[#c9a961] transition-colors" data-id="element-451">
                      {related.name}
                    </h3>
                    <p className="text-[#86868b] text-[15px] line-clamp-2 font-light" data-id="element-452">
                      {related.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}

      <Footer data-id="element-453" />
    </main>;
}